import React, { useMemo, useState } from 'react'
import { OPTIONS_VEHICLE_TYPE_INFO_VEHICLE } from '../../../../constants/vehicle'
import useLoadMetadataCache from '../../../../hooks/common/useLoadMetadataCache'
import { IconArrowDown, IconCheckCircleGreen } from '../../../../assets/icons'
import iconError from '../../../../assets/icons/icon_error.png'
import iconDanger from '../../../../assets/icons/icon_danger.png'
import { formatTimeAndDate } from '../../../../helper/formatDate'
import SafeImage from '../../../../components/BasicComponent/SafeImage'
import useThemeFeatureConfig from '../../../../hooks/common/useThemeFeatureConfig'
import './index.scss'

const FALLBACK_CRIMINAL_ORIGINS = {
  TTDK: {
    criminalOriginValue: 1,
    criminalOriginName: 'Hệ thống TTDK'
  },
  VIETNAM_REGISTER: {
    criminalOriginValue: 2,
    criminalOriginName: 'Cục Đăng kiểm'
  },
  VEHICLE_CONDITION: {
    criminalOriginValue: 3,
    criminalOriginName: 'Tình trạng xe'
  },
  CSGT: {
    criminalOriginValue: 4,
    criminalOriginName: 'Cảnh sát giao thông'
  },
  CSGT_EHUB: {
    criminalOriginValue: 5,
    criminalOriginName: 'Cảnh sát giao thông'
  },
  CSGT_XCG: {
    criminalOriginValue: 6,
    criminalOriginName: 'Cảnh sát giao thông'
  },
  CSGT_VN: {
    criminalOriginValue: 7,
    criminalOriginName: 'Cảnh sát giao thông'
  },
  CSGT_DANGKIEM: {
    criminalOriginValue: 8,
    criminalOriginName: 'Cảnh sát giao thông'
  }
}

const CRIMINAL_STATUS = {
  NO: 'Chưa xử phạt',
  YES: 'Đã xử phạt',
  NOT_CRIMINAL: 'Không vi phạm',
  API_ERROR: 'Lỗi tra cứu'
}

function isFilled(value) {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  return true
}

function getVehicleTypeLabel(vehicleType) {
  const matchedVehicle = OPTIONS_VEHICLE_TYPE_INFO_VEHICLE.find((item) => String(item.value) === String(vehicleType))

  if (matchedVehicle?.label) {
    return matchedVehicle.label
  }

  return isFilled(vehicleType) ? String(vehicleType).trim() : ''
}

function getCrimeRecordTimeLabel(crimeRecordTime) {
  const formattedTime = formatTimeAndDate(crimeRecordTime)
  return formattedTime && formattedTime !== '—' ? formattedTime : ''
}

function createCriminalOriginLookup(metadata) {
  const source = { 
    ...FALLBACK_CRIMINAL_ORIGINS
   }
  const metadataOrigins = metadata?.CRIMINAL_ORIGIN

  if (metadataOrigins && typeof metadataOrigins === 'object') {
    Object.entries(metadataOrigins).forEach(([key, item]) => {
      source[key] = {
        ...(source[key] || {}),
        ...item
      }
    })
  }

  return Object.entries(source).reduce((lookup, [key, item]) => {
    const originName = typeof item?.criminalOriginName === 'string' ? item.criminalOriginName.trim() : item?.criminalOriginName
    const originValue = item?.criminalOriginValue

    if (isFilled(originName)) {
      lookup[key] = originName
    }

    if (isFilled(originValue) && isFilled(originName)) {
      lookup[String(originValue)] = originName
    }

    return lookup
  }, {})
}

function getCrimeRecordOriginLabel(crimeRecordOrigin, criminalOriginLookup) {
  if (!isFilled(crimeRecordOrigin)) {
    return ''
  }

  const originKey = String(crimeRecordOrigin).trim()
  return criminalOriginLookup[originKey] || ''
}

function CriminalRecordField({ label, value }) {
  const displayValue = typeof value === 'string' ? value.trim() : value

  if (!isFilled(displayValue)) {
    return null
  }

  return (
    <div className="ListCriminalRecords_field">
      <div className="ListCriminalRecords_fieldLabel">{label}</div>
      <div className="ListCriminalRecords_fieldValue">{displayValue}</div>
    </div>
  )
}

function CriminalRecordItem({ item, index, vehicleTypeLabel, criminalOriginLookup, defaultOpen = false }) {
  const { showHeaderIcon, showStatusIcon } = useThemeFeatureConfig('LIST_CRIMINAL_RECORDS')
  const [open, setOpen] = useState(defaultOpen)
  const timeLabel = getCrimeRecordTimeLabel(item?.crimeRecordTime)
  const statusLabel = typeof item?.crimeRecordStatus === 'string' ? item.crimeRecordStatus.trim() : item?.crimeRecordStatus
  const isSuccessStatus = statusLabel === CRIMINAL_STATUS.YES
  const isNoPunishStatus = statusLabel === CRIMINAL_STATUS.NO
  const fields = [
    { label: 'Loại phương tiện', value: vehicleTypeLabel },
    { label: 'Màu biển số', value: item?.customerRecordPlateColorDesc },
    { label: 'Địa điểm vi phạm', value: item?.crimeRecordLocation },
    { label: 'Hành vi vi phạm', value: item?.crimeRecordContent },
    { label: 'Đơn vị phát hiện vi phạm', value: item?.crimeRecordPIC },
    { label: 'Nơi giải quyết vụ việc', value: item?.crimeRecordAgency },
    { label: 'Nguồn tra cứu', value: getCrimeRecordOriginLabel(item?.crimeRecordOrigin, criminalOriginLookup) }
  ].filter((field) => isFilled(field.value))

  const hasExpandableContent = isFilled(statusLabel) || fields.length > 0
  const isOpen = hasExpandableContent && open

  return (
    <div className={`ListCriminalRecords_item ${isOpen ? 'ListCriminalRecords_item_open' : ''}`}>
      <button
        type="button"
        className="ListCriminalRecords_itemHeader"
        onClick={() => {
          if (hasExpandableContent) {
            setOpen((prevOpen) => !prevOpen)
          }
        }}
        aria-expanded={isOpen}
      >
        {showHeaderIcon ? (
          <div className="ListCriminalRecords_headerIconWrap">
            <SafeImage src={iconError} />
          </div>
        ) : null}

        <div className="ListCriminalRecords_headerMain">
          <div className="ListCriminalRecords_headerTitle">Lỗi {index + 1}</div>
          {isFilled(timeLabel) ? <div className="ListCriminalRecords_headerSub">Vi phạm lúc {timeLabel}</div> : null}
        </div>

        {hasExpandableContent ? (
          <span className="ListCriminalRecords_arrow">
            <IconArrowDown />
          </span>
        ) : null}
      </button>

      {hasExpandableContent ? (
        <div className="ListCriminalRecords_collapse">
          <div className="ListCriminalRecords_collapseInner">
            {isFilled(statusLabel) ? (
              <div className="ListCriminalRecords_statusRow">
                <div className="ListCriminalRecords_fieldLabel">Trạng thái</div>
                <div className={`ListCriminalRecords_statusChip ${isSuccessStatus ? 'ListCriminalRecords_statusChip_success' : ''} ${isNoPunishStatus ? 'ListCriminalRecords_statusChip_punish' : ''}`}>
                  {showStatusIcon ? (
                    isSuccessStatus ? <IconCheckCircleGreen aria-hidden="true" /> : <SafeImage src={iconDanger} />
                  ) : null}
                  <span>{statusLabel}</span>
                </div>
              </div>
            ) : null}

            {fields.map((field) => (
              <CriminalRecordField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default function ListCriminalRecords({ autoOpen = true, vehicleIdentity = '', vehicleType, criminalData = [] }) {
  const { metadata } = useLoadMetadataCache()
  const vehicleTypeLabel = getVehicleTypeLabel(vehicleType)
  const criminalOriginLookup = useMemo(() => createCriminalOriginLookup(metadata), [metadata])
  const criminalRecords = Array.isArray(criminalData) ? criminalData : []

  if (!criminalRecords.length) {
    return null
  }

  return (
    <div className="ListCriminalRecords">
      {criminalRecords.map((item, index) => (
        <CriminalRecordItem
          key={`${vehicleIdentity || 'vehicle'}-${index}`}
          item={item}
          index={index}
          vehicleTypeLabel={vehicleTypeLabel}
          criminalOriginLookup={criminalOriginLookup}
          defaultOpen={autoOpen ? index === 0 : false}
        />
      ))}
    </div>
  )
}

